/**
 * SVG文件加载和处理服务
 */

// SVG文件缓存
const svgCache = new Map<string, string>();

/**
 * 从外部SVG文件加载省份轮廓路径
 * @param provinceName 省份名称 (如: "北京市", "广东省")
 * @returns Promise<string | null> SVG路径字符串或null
 */
export const loadProvinceSVGPath = async (provinceName: string): Promise<string | null> => {
  // 检查缓存
  if (svgCache.has(provinceName)) {
    return svgCache.get(provinceName) || null;
  }

  try {
    // 构建SVG文件URL
    const svgUrl = `/data/${encodeURIComponent(provinceName)}.svg`;
    
    console.log(`🔍 尝试加载外部SVG文件: ${svgUrl}`);
    
    // 获取SVG文件
    const response = await fetch(svgUrl);
    
    if (!response.ok) {
      console.warn(`❌ SVG文件不存在: ${svgUrl} (${response.status})`);
      svgCache.set(provinceName, ''); // 缓存失败结果，避免重复请求
      return null;
    }

    const svgContent = await response.text();
    
    // 提取SVG中的path元素的d属性
    const pathMatch = svgContent.match(/<path[^>]+d="([^"]+)"/);
    
    if (!pathMatch || !pathMatch[1]) {
      console.warn(`❌ SVG文件中未找到有效的path元素: ${svgUrl}`);
      svgCache.set(provinceName, '');
      return null;
    }

    const pathData = pathMatch[1];
    console.log(`✅ 成功加载外部SVG: ${provinceName}`);
    
    // 缓存结果
    svgCache.set(provinceName, pathData);
    
    return pathData;
  } catch (error) {
    console.warn(`❌ 加载SVG文件失败: ${provinceName}`, error);
    svgCache.set(provinceName, ''); // 缓存失败结果
    return null;
  }
};

/**
 * 批量预加载SVG文件
 * @param provinceNames 省份名称数组
 */
export const preloadProvinceSVGs = async (provinceNames: string[]): Promise<void> => {
  console.log(`🚀 开始预加载 ${provinceNames.length} 个省份的SVG文件`);
  
  const loadPromises = provinceNames.map(async (provinceName) => {
    try {
      await loadProvinceSVGPath(provinceName);
    } catch (error) {
      console.warn(`预加载SVG失败: ${provinceName}`, error);
    }
  });

  await Promise.allSettled(loadPromises);
  console.log(`✅ SVG文件预加载完成`);
};

/**
 * 清除SVG缓存
 */
export const clearSVGCache = (): void => {
  svgCache.clear();
  console.log('🗑️ SVG缓存已清除');
};

/**
 * 获取缓存状态
 */
export const getSVGCacheStatus = (): { total: number; loaded: number; failed: number } => {
  const entries = Array.from(svgCache.entries());
  const loaded = entries.filter(([_, path]) => path !== '').length;
  const failed = entries.filter(([_, path]) => path === '').length;
  
  return {
    total: entries.length,
    loaded,
    failed
  };
};

/**
 * 验证SVG路径数据的有效性
 * @param pathData SVG路径字符串
 * @returns boolean 是否有效
 */
export const validateSVGPath = (pathData: string): boolean => {
  if (!pathData || typeof pathData !== 'string') {
    return false;
  }

  // 基本的SVG路径格式检查
  const svgPathRegex = /^[MmLlHhVvCcSsQqTtAaZz0-9\s,.-]+$/;
  return svgPathRegex.test(pathData.trim());
};

/**
 * 标准化SVG路径数据
 * @param pathData 原始SVG路径
 * @returns string 标准化后的路径
 */
export const normalizeSVGPath = (pathData: string): string => {
  return pathData
    .trim()
    .replace(/\s+/g, ' ')  // 合并多个空格
    .replace(/,\s*/g, ',') // 标准化逗号分隔符
    .replace(/\s*([MmLlHhVvCcSsQqTtAaZz])\s*/g, ' $1 ') // 标准化命令符号
    .trim();
};
